import { HyruleToken } from '@nintendo/hyrule-react-commons';
import dayjs from 'dayjs';
import { GameDetails, GameSummary, GameSummaryApiModel } from './models';
import { BaseApi } from './BaseApi';

import Utils from '../shared/Utils';
import { GamePlayDataModel } from '../components/account/game-play/GamePlayDataModel';

export class GamePlaySummaryApi {
    static async getGameHistorySummaries(
        naid: string,
        token: HyruleToken,
        correlationId: string,
    ): Promise<GameSummaryApiModel> {
        const { accountApiUrls } = BaseApi.ApiConfig();
        const url: string = accountApiUrls.gameSummaries(naid);
        const init: RequestInit = await BaseApi.MulesoftRequestInit('GET', token, correlationId);

        try {
            const response: Response = await fetch(url, init);

            if (response.status === 570) {
                return {
                    optedOut: true,
                    lastUpdatedAt: 0,
                    playedTitles: [],
                };
            }

            return await response.json();
        } catch (error) {
            console.log(`Error: ${error}`);
            throw error;
        }
    }

    private static consolidateSummaries(summaries: GameSummary[]): GameSummary[] {
        const consolidatedDict: { [applicationId: string]: GameSummary } = {};

        for (let i = 0; i < summaries.length; i++) {
            const summary = summaries[i];
            const existingSummary: GameSummary = consolidatedDict[summary.titleId];

            if (existingSummary) {
                existingSummary.totalPlayedDays += summary.totalPlayedDays;
                existingSummary.totalPlayedMinutes += summary.totalPlayedMinutes;

                const lastPlayedIncoming: dayjs.Dayjs = dayjs.unix(summary.lastPlayedAt);
                const lastPlayedCurrent: dayjs.Dayjs = dayjs.unix(existingSummary.lastPlayedAt);
                if (lastPlayedIncoming.isAfter(lastPlayedCurrent)) {
                    existingSummary.lastPlayedAt = summary.lastPlayedAt;
                }
            } else {
                consolidatedDict[summary.titleId] = summary;
            }
        }

        return Object.values(consolidatedDict);
    }

    static mapToGamePlayDataModelList(
        model: GameSummaryApiModel,
        naid: string,
        gameDetails: GameDetails[],
    ): GamePlayDataModel[] {
        const consolidatedSummaries: GameSummary[] = GamePlaySummaryApi.consolidateSummaries(
            model.playedTitles,
        );

        return consolidatedSummaries
            .sort((a, b) => b.lastPlayedAt - a.lastPlayedAt)
            .map((summary) => {
                const { hours, minutes } = Utils.minutesToHoursAndMinutes(
                    summary.totalPlayedMinutes,
                );
                const playTime: string = hours > 0 ? `${hours}h, ${minutes}m` : `${minutes}m`;

                const gameDetail: GameDetails | undefined = gameDetails.find(
                    (detail) => detail.appId === summary.titleId,
                );

                return {
                    playTime,
                    applicationId: summary.titleId,
                    consumerId: naid,
                    title: gameDetail ? gameDetail.name : 'Unknown Game',
                    imageUrl: gameDetail ? gameDetail.productImage.url : '',
                };
            });
    }
}
